import { useState, type FormEvent, type ReactElement } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchPricing, postPricing, type PricingRow } from '../../api/admin.js';
import { nanoToUsd, formatDateTime, type NanoLike } from '../../lib/format.js';

/**
 * Las tarifas en DB están guardadas en *nano-USD* (1 USD = 10^8 nano, escala
 * heredada de `Money.SCALE`). Acá las renderizamos como USD por la unidad
 * comercial de cada `service_kind`:
 *  - LLM:  $X.XX / MTok (input ya viene "por MTok", solo dividimos por escala)
 *  - TTS:  $X.XX / 1k chars (más legible que $/char)
 *  - STT / pronunciation: $X.XX / h (más legible que $/s)
 *  - voice (Vapi): $X.XX / min
 */
function usdPerMtok(value: NanoLike): string {
  return value === null || value === undefined || value === '' ? '—' : nanoToUsd(value, 4);
}

function usdPer1kChars(nanoPerChar: NanoLike): string {
  if (nanoPerChar === null || nanoPerChar === undefined || nanoPerChar === '') return '—';
  try {
    const n = typeof nanoPerChar === 'bigint' ? nanoPerChar : BigInt(String(nanoPerChar));
    return nanoToUsd((n * 1000n).toString(), 4);
  } catch {
    return '—';
  }
}

function usdPerHour(nanoPerSecond: NanoLike): string {
  if (nanoPerSecond === null || nanoPerSecond === undefined || nanoPerSecond === '') return '—';
  try {
    const n = typeof nanoPerSecond === 'bigint' ? nanoPerSecond : BigInt(String(nanoPerSecond));
    return nanoToUsd((n * 3600n).toString(), 4);
  } catch {
    return '—';
  }
}

export function PricingPage(): ReactElement {
  const query = useQuery({ queryKey: ['admin', 'pricing'], queryFn: fetchPricing });
  const [showForm, setShowForm] = useState(false);

  return (
    <>
      <div className="page-header">
        <div>
          <h1 className="page-title">Pricing</h1>
          <p className="page-sub">Append-only · cada nueva versión deja la anterior como histórico</p>
        </div>
        <div>
          <button type="button" className="btn-primary" onClick={() => setShowForm((v) => !v)}>
            {showForm ? 'Cancelar' : 'Nueva versión'}
          </button>
        </div>
      </div>
      {showForm && <NewPricingForm onDone={() => setShowForm(false)} />}
      {query.isPending ? (
        <div className="loading">Cargando…</div>
      ) : query.isError ? (
        <div className="loading">Error: {(query.error).message}</div>
      ) : (
        <section className="card">
          <table className="data-table">
            <thead>
              <tr>
                <th>Provider</th>
                <th>Model</th>
                <th>Service</th>
                <th className="r">Input <small>$/MTok</small></th>
                <th className="r">Output <small>$/MTok</small></th>
                <th className="r">TTS <small>$/1k chars</small></th>
                <th className="r">STT <small>$/h</small></th>
                <th>Status</th>
                <th>Effective</th>
              </tr>
            </thead>
            <tbody>
              {query.data.rows.length === 0 ? (
                <tr>
                  <td colSpan={9} className="empty">Sin pricing</td>
                </tr>
              ) : (
                query.data.rows.map((r) => <PricingRowEl key={r.id} row={r} />)
              )}
            </tbody>
          </table>
        </section>
      )}
    </>
  );
}

function PricingRowEl({ row }: { row: PricingRow }): ReactElement {
  return (
    <tr className={row.active ? 'pricing-active' : 'pricing-inactive'}>
      <td>{row.provider}</td>
      <td className="mono">{row.model}</td>
      <td>
        <span className={`kind-pill kind-${row.serviceKind}`}>{row.serviceKind}</span>
      </td>
      <td className="r mono">{usdPerMtok(row.inputNanoPerMtok)}</td>
      <td className="r mono">{usdPerMtok(row.outputNanoPerMtok)}</td>
      <td className="r mono">{usdPer1kChars(row.nanoPerChar)}</td>
      <td className="r mono">{usdPerHour(row.nanoPerSecond)}</td>
      <td>{row.active ? <span className="pill pill-good">active</span> : <span className="pill">old</span>}</td>
      <td className="tt-tert">{formatDateTime(row.effectiveFrom)}</td>
    </tr>
  );
}

function NewPricingForm({ onDone }: { onDone: () => void }): ReactElement {
  const qc = useQueryClient();
  const [provider, setProvider] = useState('');
  const [model, setModel] = useState('');
  const [serviceKind, setServiceKind] = useState('llm');
  const [inputNanoPerMtok, setInput] = useState('');
  const [outputNanoPerMtok, setOutput] = useState('');
  const [notes, setNotes] = useState('');
  const mut = useMutation({
    mutationFn: postPricing,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'pricing'] });
      onDone();
    },
  });
  const submit = (e: FormEvent): void => {
    e.preventDefault();
    mut.mutate({
      provider,
      model,
      serviceKind,
      inputNanoPerMtok: inputNanoPerMtok || undefined,
      outputNanoPerMtok: outputNanoPerMtok || undefined,
      notes: notes || undefined,
    });
  };
  return (
    <section className="card form-card">
      <h3>Nueva versión</h3>
      <form onSubmit={submit} className="form-grid">
        <label>Provider <input value={provider} onChange={(e) => setProvider(e.target.value)} required /></label>
        <label>Model <input value={model} onChange={(e) => setModel(e.target.value)} required /></label>
        <label>Service
          <select value={serviceKind} onChange={(e) => setServiceKind(e.target.value)}>
            <option value="llm">llm</option>
            <option value="tts">tts</option>
            <option value="stt">stt</option>
            <option value="voice">voice</option>
          </select>
        </label>
        <label>Input nano/Mtok <input value={inputNanoPerMtok} onChange={(e) => setInput(e.target.value)} /></label>
        <label>Output nano/Mtok <input value={outputNanoPerMtok} onChange={(e) => setOutput(e.target.value)} /></label>
        <label className="form-row-2">Notes <input value={notes} onChange={(e) => setNotes(e.target.value)} /></label>
        <button type="submit" className="btn-primary" disabled={mut.isPending}>
          {mut.isPending ? 'Guardando…' : 'Insertar'}
        </button>
        {mut.isError && <p className="error">{(mut.error).message}</p>}
      </form>
    </section>
  );
}
